import React, { useState, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { FIELD_TYPES } from '../../lib/constants';
import {
  DuotoneCircleIcon, DuotoneArrowUpIcon, DuotoneArrowDownIcon,
  CircleIcon, ArrowUpIcon, ArrowDownIcon,
} from '../../icons';
import { getActiveIndicatorPalette, isColorBlindModeEnabled } from '../../theme/colorBlindMode';
// DEV-838 / DEV-840: Single source of truth for the Mission/Summary
// adjustment pipeline. Any surface that renders mission_indicator_color /
// summary_indicator_color MUST go through these helpers. The helper
// returns an `adjustments[]` list — today only HSE floor cap can populate
// it; viewer trust overlay (DEV-840) will append a second variant later.
// See src/Data/displayKpiState.ts for the contract.
import {
  getMissionDisplayState,
  getSummaryDisplayState,
  getDataReliabilityDisplayState,
  describeAdjustment,
  type DisplayKpiState,
} from '../../Data/displayKpiState';
import styles from './KpiSummaryHeader.module.css';

/**
 * KpiSummaryHeader — Option A hero row (persistent tab navigation).
 *
 * Layout per cell (vertical stack):
 *   - Eyebrow label on top
 *   - Duotone shape + score side by side below
 *
 * Score display toggles between letter grade (default) and numeric
 * on click. Preference persisted in localStorage.
 *
 * Responsive progressive disclosure:
 *   - ≤900px:  hide "/100" unit (numeric mode)
 *   - ≤760px:  hide label, show shape + score
 *   - ≤560px:  compact shape size
 *
 * Clicking a cell navigates tabs; clicking the score area toggles format.
 *   0=Summary, 1=Schedule, 2=Budget, 3=Mission, 4=Data
 */

type Props = {
  /**
   * The project row. SHOULD have `hse_bundle` attached when the floor cap
   * needs to apply. KpiNavigation attaches it before passing the item in;
   * other surfaces (KpiGridTable rows) attach it via the same path. The
   * helpers in `displayKpiState.ts` no-op cleanly when the bundle is absent.
   */
  selectedItem: any;
  activeTab: number;
  onSelectTab: (tabIndex: number) => void;
};

type Cell = {
  idx: number;
  labelKey: string;
  type: string;
  colorKey: string;
  trendKey: string;
  scoreKey: string | null;
};

const CELLS: Cell[] = [
  { idx: 0, labelKey: 'summary',         type: FIELD_TYPES.SUMMARY,
    colorKey: 'summary_indicator_color',  trendKey: 'summary_indicator_trend',
    scoreKey: 'summary_score_0_100' },
  { idx: 1, labelKey: 'schedule',        type: FIELD_TYPES.SCHEDULE,
    colorKey: 'schedule_indicator_color', trendKey: 'schedule_indicator_trend',
    scoreKey: 'schedule_score_0_100' },
  { idx: 2, labelKey: 'finance',         type: FIELD_TYPES.FINANCE,
    colorKey: 'finance_indicator_color',  trendKey: 'finance_indicator_trend',
    scoreKey: 'finance_score_0_100' },
  { idx: 3, labelKey: 'mission',         type: FIELD_TYPES.MISSION,
    colorKey: 'mission_indicator_color',  trendKey: 'mission_indicator_trend',
    scoreKey: 'mission_score_0_100' },
  { idx: 4, labelKey: 'dataReliability', type: FIELD_TYPES.DATA,
    colorKey: 'data_reliability_color',   trendKey: 'data_reliability_trend',
    scoreKey: 'data_reliability_score_0_100' },
];

const safeNum = (v: any): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* ── Score-to-grade conversion ── */

const LS_KEY = 'l4e_score_format';

type ScoreFormat = 'grade' | 'numeric';

function readScoreFormat(): ScoreFormat {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'numeric') return 'numeric';
  } catch { /* SSR / quota */ }
  return 'grade';
}

function persistScoreFormat(fmt: ScoreFormat): void {
  try { localStorage.setItem(LS_KEY, fmt); } catch { /* quota */ }
}

function scoreToGrade(score: number): string {
  const s = Math.round(score);
  if (s >= 97) return 'A+';
  if (s >= 93) return 'A';
  if (s >= 90) return 'A-';
  if (s >= 87) return 'B+';
  if (s >= 83) return 'B';
  if (s >= 80) return 'B-';
  if (s >= 77) return 'C+';
  if (s >= 73) return 'C';
  if (s >= 70) return 'C-';
  if (s >= 60) return 'D';
  return 'F';
}

/* ── Indicator helpers ── */

const bucketColor = (code: any, completed: boolean) => {
  const p = getActiveIndicatorPalette();
  if (completed) return p.completed;
  const n = safeNum(code);
  if (n === null) return p.grey;
  if (n === 0) return p.grey;
  if (n === 1) return p.red;
  if (n === 2) return p.yellow;
  if (n === 3) return p.green;
  if (n <= 0.5) return p.grey;
  if (n <= 1.5) return p.red;
  if (n <= 2.5) return p.yellow;
  return p.green;
};

const ShapeFor = ({ trend, completed, color, size }: {
  trend: number | null; completed: boolean; color: string; size: number;
}) => {
  const solid = isColorBlindModeEnabled();
  // DEV-966 follow-up. Removed `completed ||` from the circle check.
  // Per the agreed spec, completed projects keep their natural trend
  // shape (▲ ● ▼) — only the color changes to blue (handled in the
  // `color` prop by the caller via defineIndicatorColor). This was a
  // duplicate shape-rendering path that wasn't updated when
  // defineIndicatorIcon in helpers.tsx was fixed, causing the panel
  // to render all circles while the KpiGridTable cells correctly
  // showed natural shapes. Both surfaces now consistent.
  if (trend === 0 || trend === null) {
    return solid
      ? <CircleIcon color={color} height={size} width={size} />
      : <DuotoneCircleIcon color={color} height={size} width={size} />;
  }
  if (trend === -1) {
    return solid
      ? <ArrowDownIcon color={color} height={size} width={size} />
      : <DuotoneArrowDownIcon color={color} height={size} width={size} />;
  }
  if (trend === 1) {
    return solid
      ? <ArrowUpIcon color={color} height={size} width={size} />
      : <DuotoneArrowUpIcon color={color} height={size} width={size} />;
  }
  return solid
    ? <CircleIcon color={color} height={size} width={size} />
    : <DuotoneCircleIcon color={color} height={size} width={size} />;
};

/* ── Component ── */

const KpiSummaryHeader: React.FC<Props> = ({ selectedItem, activeTab, onSelectTab }) => {
  const { t } = useAppTranslation('common');
  const tKpi = useAppTranslation('kpi').t;
  const item: any = selectedItem ?? {};
  const projectStatus = String(item?.project?.project_status ?? '').toLowerCase();
  const isProjectCompleted = projectStatus === 'completed';

  const [scoreFormat, setScoreFormat] = useState<ScoreFormat>(readScoreFormat);

  const toggleFormat = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const next: ScoreFormat = scoreFormat === 'grade' ? 'numeric' : 'grade';
    setScoreFormat(next);
    persistScoreFormat(next);
  }, [scoreFormat]);

  // DEV-838: Single source of truth for the HSE floor-cap rule.
  // We compute the display state for Mission and Summary up front; the cell
  // render loop reads from these instead of raw item fields. If you need to
  // add a new cell that participates in the floor cap, add a getter to
  // `src/Data/displayKpiState.ts` — do NOT re-derive the rule here.
  const missionDisplay: DisplayKpiState = getMissionDisplayState(item);
  const summaryDisplay: DisplayKpiState = getSummaryDisplayState(item);
  // DEV-851: Single source of truth for the blue Complete-state override on
  // the Data Reliability cell. Helper reads `_isCompletedProject` from the
  // row (set by App.tsx) and exposes `isComplete: true` when the project is
  // closed. Renderer below uses isComplete to flip the cell to blue Complete.
  const dataReliabilityDisplay: DisplayKpiState = getDataReliabilityDisplayState(item);

  return (
    <div className={styles.row} role="tablist" aria-label={t('kpiCategory.summary')}>
      {CELLS.map((cell) => {
        // Pull from the helper for Mission/Summary/DataReliability; fall back
        // to raw fields for the other (currently un-adjusted) cells.
        const display: DisplayKpiState | null =
          cell.idx === 0 ? summaryDisplay :
          cell.idx === 3 ? missionDisplay :
          cell.idx === 4 ? dataReliabilityDisplay :
          null;

        const colorCode = display ? display.color : item?.[cell.colorKey];
        const trend = display ? display.trend : safeNum(item?.[cell.trendKey]);

        // DEV-966. Completion override now applies to ALL five KPI cells
        // (not just Summary + Data). When the project is completed, the
        // cell renders BLUE and the score/grade is preserved. The natural
        // trend shape is unchanged. No "Complete" label — blue is the
        // only completion signal. Was: only Summary checked
        // isProjectCompleted; only Data swapped its score for a
        // "Complete" label.
        const completed =
          isProjectCompleted ||
          (cell.type === FIELD_TYPES.DATA && !!display?.isComplete);
        const color = bucketColor(colorCode, completed);

        const score = display
          ? display.score
          : (cell.scoreKey ? safeNum(item?.[cell.scoreKey]) : null);

        // DEV-966. Score rendering no longer special-cases completion.
        // The engine may still return null for some completed-state KPI
        // (e.g., Data Reliability under the new lifecycle gate) in which
        // case we show '—' as we would for any other null score; that's
        // honest about the absence of data without inventing a label.
        let valueText: string;
        let showUnit = false;
        if (score === null) {
          valueText = '—';
        } else if (scoreFormat === 'grade') {
          valueText = scoreToGrade(score);
        } else {
          valueText = String(Math.round(score));
          showUnit = true;
        }

        const isActive = activeTab === cell.idx;

        // DEV-840: adjustments-driven badge + tooltip. Localize each
        // adjustment via describeAdjustment(); join with newlines so a
        // future cell with multiple adjustments lists them readably in
        // the native tooltip. The badge itself is always a single dot;
        // we don't try to encode "count" visually, because in practice
        // 1 vs N adjustments matters less than "any vs none".
        const adjustments = display?.adjustments ?? [];
        const adjustmentLabels = adjustments
          .map((a) => describeAdjustment(a, tKpi as any))
          .filter((s): s is string => !!s);
        const tooltip = adjustmentLabels.length > 0
          ? adjustmentLabels.join('\n')
          : undefined;

        return (
          <button
            key={cell.idx}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`${styles.cell} ${isActive ? styles.active : ''}`}
            onClick={() => onSelectTab(cell.idx)}
            style={{ ['--cell-accent' as any]: color }}
            title={tooltip}
          >
            {adjustmentLabels.length > 0 && (
              <span
                className={styles.adjustmentBadge}
                aria-label={tooltip}
                role="img"
              >
                !
              </span>
            )}
            <span className={styles.eyebrow}>
              {t(`kpiCategory.${cell.labelKey}`)}
            </span>
            <span className={styles.bottom}>
              <span className={styles.shape} aria-hidden="true">
                <ShapeFor trend={trend} completed={completed} color={color} size={36} />
              </span>
              <span
                className={styles.valueRow}
                onClick={toggleFormat}
                role="button"
                tabIndex={-1}
                aria-label={scoreFormat === 'grade' ? 'Switch to numeric score' : 'Switch to letter grade'}
              >
                <span className={styles.value}>{valueText}</span>
                {showUnit && cell.scoreKey ? <span className={styles.unit}>/100</span> : null}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default KpiSummaryHeader;
